import React from 'react';
import {
  OFFICIAL_BUYER_PORTAL_URL,
  OFFICIAL_PLATFORM_URL,
  getCurrentHostname,
  isOfficialCassavaChainHost,
} from './officialDomains';

const pageStyle = {
  minHeight: '100dvh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 18px',
  background: 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.16), transparent 30%), linear-gradient(135deg, #f8fbff 0%, #eef4ff 46%, #f7fafc 100%)',
  color: '#0f172a',
  fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const cardStyle = {
  width: 'min(760px, 100%)',
  borderRadius: 28,
  border: '1px solid rgba(148, 163, 184, 0.38)',
  background: 'rgba(255, 255, 255, 0.94)',
  boxShadow: '0 28px 90px rgba(15, 23, 42, 0.16)',
  padding: 'clamp(24px, 5vw, 44px)',
};

const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  borderRadius: 999,
  border: '1px solid rgba(37, 99, 235, 0.18)',
  background: 'rgba(37, 99, 235, 0.08)',
  color: '#1d4ed8',
  fontSize: 13,
  fontWeight: 800,
  padding: '8px 12px',
  letterSpacing: '0.02em',
};

const titleStyle = {
  margin: '22px 0 10px',
  fontSize: 'clamp(30px, 5vw, 48px)',
  lineHeight: 1.05,
  fontWeight: 900,
  letterSpacing: '-0.04em',
};

const sectionStyle = {
  borderTop: '1px solid rgba(148, 163, 184, 0.24)',
  paddingTop: 18,
  marginTop: 18,
};

const languageTitleStyle = {
  margin: '0 0 8px',
  fontSize: 16,
  lineHeight: 1.35,
  fontWeight: 900,
};

const textStyle = {
  margin: 0,
  fontSize: 15,
  lineHeight: 1.75,
  color: '#475569',
};

const buttonRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  marginTop: 26,
};

const buttonBaseStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 46,
  borderRadius: 999,
  padding: '0 18px',
  fontSize: 14,
  fontWeight: 900,
  textDecoration: 'none',
};

const primaryButtonStyle = {
  ...buttonBaseStyle,
  color: '#ffffff',
  background: '#2563eb',
  boxShadow: '0 14px 26px rgba(37, 99, 235, 0.24)',
};

const secondaryButtonStyle = {
  ...buttonBaseStyle,
  color: '#1d4ed8',
  background: '#ffffff',
  border: '1px solid rgba(37, 99, 235, 0.28)',
};

const hostStyle = {
  display: 'inline-flex',
  marginTop: 18,
  maxWidth: '100%',
  borderRadius: 14,
  background: '#f1f5f9',
  color: '#334155',
  padding: '8px 11px',
  fontSize: 13,
  fontWeight: 800,
  overflowWrap: 'anywhere',
};

function UnofficialDomainBlocked({ hostname = '' }) {
  const safeHost = String(hostname || 'unknown domain').trim() || 'unknown domain';

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    document.title = 'Unofficial Domain Blocked | CassavaChain';
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex,nofollow');
  }, []);

  return (
    <main style={pageStyle} aria-labelledby="unofficial-domain-title">
      <section style={cardStyle}>
        <div style={badgeStyle}>CassavaChain Security Guard</div>
        <h1 id="unofficial-domain-title" style={titleStyle}>Unofficial Domain Blocked</h1>
        <p style={textStyle}>
          This page is not being served from an official CassavaChain domain. For your account safety,
          sign-in is disabled on this address.
        </p>
        <div style={hostStyle}>Current domain: {safeHost}</div>

        <div style={sectionStyle} lang="zh-CN">
          <h2 style={languageTitleStyle}>非官方域名已阻断</h2>
          <p style={textStyle}>当前页面并非通过 CassavaChain 官方域名访问。为保护账号安全，此地址已禁用登录入口。请通过 CassavaChain 官方入口继续访问。</p>
        </div>

        <div style={sectionStyle} lang="am">
          <h2 style={languageTitleStyle}>ያልተፈቀደ ዶሜይን ታግዷል</h2>
          <p style={textStyle}>ይህ ገጽ ከ CassavaChain ኦፊሴላዊ ዶሜይን አይቀርብም። የመለያዎን ደህንነት ለመጠበቅ በዚህ አድራሻ መግቢያ ተሰናክሏል። እባክዎ በኦፊሴላዊ የ CassavaChain መግቢያዎች ይቀጥሉ።</p>
        </div>

        <div style={buttonRowStyle} aria-label="Official CassavaChain portals">
          <a style={primaryButtonStyle} href={OFFICIAL_PLATFORM_URL} rel="noopener noreferrer">
            Open Official Platform / 打开官方平台
          </a>
          <a style={secondaryButtonStyle} href={OFFICIAL_BUYER_PORTAL_URL} rel="noopener noreferrer">
            Open Buyer Portal / 打开买家入口
          </a>
        </div>
      </section>
    </main>
  );
}

export function HostGuard({ mode, children }) {
  const hostname = getCurrentHostname();
  const search = typeof window === 'undefined' ? '' : window.location?.search || '';
  if (!isOfficialCassavaChainHost(hostname, mode, { search })) {
    return <UnofficialDomainBlocked hostname={hostname} />;
  }

  return <>{children}</>;
}

export default HostGuard;
